import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { Icon, Button, ArchImage } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { trackLogin } from '../../utils/analytics';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const from = location.state?.from || '/';

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');

  const validate = () => {
    const e = {};
    if (!formData.email) e.email = t('Email is required');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = t('Enter a valid email address');
    if (!formData.password) e.password = t('Password is required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (k) => (e) => { setFormData((p) => ({ ...p, [k]: e.target.value })); setErrors((p) => ({ ...p, [k]: '' })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setLoginError('');
    try {
      await login(formData);
      trackLogin('email');
      navigate(from, { replace: true });
    } catch (err) {
      setLoginError(err.response?.data?.message || t('Login failed. Please check your credentials.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Sign in" description="Sign in to your AyodhyaDham.info account to manage darshan, puja and hotel bookings." canonicalUrl="/login" noindex />

      <div className="auth">
        <div className="auth__panel">
          <div className="auth__box">
            <div className="auth__head">
              <span className="auth__mark"><Icon name="namaste" size={26} /></span>
              <h1 className="auth__title">{t('Welcome back')}</h1>
              <p className="auth__sub">{t('Sign in to manage your bookings and sankalp')}</p>
            </div>

            <form className="card auth__card" onSubmit={handleSubmit} noValidate>
              {loginError && (
                <div className="notice notice--danger"><Icon name="circle-alert" size={18} /><span>{loginError}</span></div>
              )}

              <label className="field">
                <span className="field__label">{t('Email address')}</span>
                <span className={`control${errors.email ? ' is-invalid' : ''}`}>
                  <Icon name="mail" size={16} />
                  <input type="email" autoComplete="email" value={formData.email} onChange={set('email')} placeholder="you@example.com" disabled={submitting} />
                </span>
                {errors.email && <span className="field__error">{errors.email}</span>}
              </label>

              <label className="field">
                <span className="field__label">{t('Password')}</span>
                <span className={`control${errors.password ? ' is-invalid' : ''}`}>
                  <Icon name="lock" size={16} />
                  <input type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={formData.password} onChange={set('password')} placeholder="••••••••" disabled={submitting} />
                  <button type="button" className="pw-toggle" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? t('Hide password') : t('Show password')}>
                    <Icon name={showPassword ? 'eye-off' : 'eye'} size={16} />
                  </button>
                </span>
                {errors.password && <span className="field__error">{errors.password}</span>}
              </label>

              <label className="check">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                {t('Keep me signed in')}
              </label>

              <Button type="submit" block disabled={submitting} icon={<Icon name="log-in" size={16} />} iconPosition="left">
                {submitting ? t('Signing in…') : t('Sign in')}
              </Button>
            </form>

            <div className="auth__foot">
              <Link to="/register">{t('Create an account')}</Link>
              <Link to="/">{t('Back to home')}</Link>
            </div>
          </div>
        </div>

        <aside className="auth__art" aria-hidden="true">
          <ArchImage src="/images/ram-lala/ram-lala-01.jpg" alt="" ratio="4/5" />
          <div className="card auth__quote">
            <p className="auth__quote-text">
              <span className="hindi">राम काज कीन्हें बिनु मोहि कहाँ विश्राम</span>
              “Without serving Ram&apos;s purpose, where is rest for me?”
            </p>
            <p className="auth__quote-src">— Hanuman, Ramcharitmanas</p>
          </div>
        </aside>
      </div>
    </>
  );
}
